import { Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { TOOLTIP_MAX_MEMBERS } from './aggregate-tooltip';
import { colorForStepIdx, formatDependencyMapValue, useDependencyMapConfig } from './dependency-map-config';
import { AGGREGATE_STRIPE_OFFSET_PX } from './link-style';
import { DependencyLink, DependencyNode } from './types';

const DEFAULT_OPACITY = 0.2;
const CONNECTED_OPACITY = 0.4;
const FADED_OPACITY = 0.05;
// Higher floor for step-matched links so they stay visible through dense regions.
const STEP_DEFAULT_OPACITY = 0.7;

// Visible strokes are 0.5–6px (finicky to hover); the hit area floor makes
// the tooltip trigger reliably when the cursor is near the curve.
const LINK_HIT_AREA_MIN_PX = 12;

/**
 * Cubic Bézier between two nodes. `dy` shifts the whole curve vertically (both
 * control points), so paired-stripe aggregates stay parallel rather than
 * pinching at the midpoint.
 */
const buildLinkPath = (from: DependencyNode, to: DependencyNode, dy = 0): string => {
  const midX = (from.x + to.x) / 2;
  const fy = from.y + dy;
  const ty = to.y + dy;
  return `M ${from.x} ${fy} C ${midX} ${fy}, ${midX} ${ty}, ${to.x} ${ty}`;
};

const resolveOpacity = (matchesStep: boolean, connectedIds: Set<string> | null, connected: boolean): number => {
  if (matchesStep) {
    return connectedIds === null || connected ? STEP_DEFAULT_OPACITY : FADED_OPACITY;
  }

  if (connectedIds === null) return DEFAULT_OPACITY;

  return connected ? CONNECTED_OPACITY : FADED_OPACITY;
};

// Aggregates use their generated `Other (N)` label (their id is the synthetic
// `@@other:N`); real services use the raw id rather than the truncated label.
const nameFor = (node: DependencyNode): string => (node.isAggregate ? (node.label ?? node.id) : node.id);

interface DependencyMapLinkProps {
  link: DependencyLink;
  from: DependencyNode;
  to: DependencyNode;
  /** Ids related to the current hover; null short-circuits dimming. */
  connectedIds: Set<string> | null;
  /** Per-stripe width (not total) for aggregates; value-scaled width for regulars. */
  strokeWidth: number;
  /** Step idx range across underlying values; `min !== max` drives paired stripes. -1 = base. */
  extrema: { min: number; max: number };
  onEnter: (link: DependencyLink) => void;
  onLeave: (link: DependencyLink) => void;
}

export const DependencyMapLink = ({
  link,
  from,
  to,
  connectedIds,
  strokeWidth,
  extrema,
  onEnter,
  onLeave,
}: DependencyMapLinkProps) => {
  const { thresholds, metricLabel, unit } = useDependencyMapConfig();
  const connected = connectedIds === null || (connectedIds.has(link.from) && connectedIds.has(link.to));

  // Aggregate = 2+ members. Single-member rewrites are deliberately treated as
  // regular: paired stripes would be degenerate, and the raw value still feeds
  // the value-scaled width fine.
  const members = link.members ?? [];
  const isAggregate = members.length > 1;

  // Drives the hit area always, plus the single-stroke render for regulars.
  // Aggregates add two offset stripes on top.
  const centerlineD = buildLinkPath(from, to);

  // Aggregates count as step-matching if *any* member matches; otherwise a
  // mixed bucket would lose visual emphasis on its worst-case member.
  const matchesStep = extrema.max >= 0;
  const opacity = resolveOpacity(matchesStep, connectedIds, connected);

  const tooltipContent = isAggregate ? (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        {nameFor(from)} → {nameFor(to)}
      </Typography>
      <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary }}>
        Aggregated {members.length} links
      </Typography>
      <Stack>
        {members.slice(0, TOOLTIP_MAX_MEMBERS).map((m) => {
          const memberMetric = `${formatDependencyMapValue(m.value, unit)}${metricLabel ? ` ${metricLabel}` : ''}`;
          return (
            <Typography
              key={`${m.from}->${m.to}`}
              variant="caption"
              sx={{ color: (t) => t.palette.text.legacySecondary }}
            >
              {m.from} → {m.to}: {memberMetric}
            </Typography>
          );
        })}
        {members.length > TOOLTIP_MAX_MEMBERS && (
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary, fontStyle: 'italic' }}>
            …and {members.length - TOOLTIP_MAX_MEMBERS} more
          </Typography>
        )}
      </Stack>
    </Stack>
  ) : (
    <Stack>
      <Typography variant="body2">
        {nameFor(from)} → {nameFor(to)}
      </Typography>
      <Typography variant="caption" sx={{ color: (t) => t.palette.text.legacySecondary }}>
        {`${formatDependencyMapValue(link.value, unit)}${metricLabel ? ` ${metricLabel}` : ''}`}
      </Typography>
    </Stack>
  );

  const sharedStrokeStyle = {
    opacity,
    transition: 'opacity 200ms ease',
    pointerEvents: 'none' as const,
  };

  return (
    <Tooltip Content={tooltipContent}>
      <g onMouseEnter={() => onEnter(link)} onMouseLeave={() => onLeave(link)} style={{ cursor: 'pointer' }}>
        {/* Wide invisible centerline so hover triggers reliably; `pointerEvents: stroke`
         * receives events despite the transparent stroke. */}
        <path
          d={centerlineD}
          fill="none"
          stroke="transparent"
          strokeWidth={Math.max(strokeWidth * (isAggregate ? 2 : 1), LINK_HIT_AREA_MIN_PX)}
          style={{ pointerEvents: 'stroke' }}
        />
        {isAggregate ? (
          <>
            {/* Top stripe = max (worst) member, bottom = min (best). When all
             * members fall in the same step both stripes paint the same color;
             * the gap alone still reads as "aggregate". */}
            <path
              d={buildLinkPath(from, to, -AGGREGATE_STRIPE_OFFSET_PX)}
              fill="none"
              stroke={colorForStepIdx(extrema.max, thresholds)}
              strokeWidth={strokeWidth}
              style={sharedStrokeStyle}
            />
            <path
              d={buildLinkPath(from, to, AGGREGATE_STRIPE_OFFSET_PX)}
              fill="none"
              stroke={colorForStepIdx(extrema.min, thresholds)}
              strokeWidth={strokeWidth}
              style={sharedStrokeStyle}
            />
          </>
        ) : (
          <path
            d={centerlineD}
            fill="none"
            stroke={colorForStepIdx(extrema.max, thresholds)}
            strokeWidth={strokeWidth}
            style={sharedStrokeStyle}
          />
        )}
      </g>
    </Tooltip>
  );
};
